import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useTheme, THEMES, FONTS, FONT_SIZES, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import { UsageMeter } from "@/components/UsageMeter";
import { ReferralCard } from "@/components/ReferralCard";
import { PushNotificationCard } from "@/components/PushNotificationCard";
import { User, Shield, Trash2, LogOut, Palette, Type, Check, Volume2, Globe, Bell, ALargeSmall, Save, RotateCcw, Moon, Sun, Eye, MessageSquare, Zap, Plus, Brain, Heart, Gauge, Download, HardDrive, Clock, Fingerprint, Smartphone, X, Loader2, Sparkles, Earth, FileText, FileJson, Users, Lock, BookOpen, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n/I18nProvider";

export default function SettingsPage() {
  const navigate = useNavigate();
  const { token, user, logout, updateUser } = useAuth();
  const { theme, font, fontSize, darkMode, highContrast, reduceMotion, customTheme, setTheme, setFont, setFontSize, toggleDarkMode, toggleHighContrast, toggleReduceMotion, setCustomTheme } = useTheme();
  const { t, lang, setLang } = useI18n();
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.persona?.preferred_name || "");
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showDeleteHistory, setShowDeleteHistory] = useState(false);
  const [showCustomCreator, setShowCustomCreator] = useState(false);
  const [customPrimary, setCustomPrimary] = useState(customTheme?.primary || "#4a6fa5");
  const [customAccent, setCustomAccent] = useState(customTheme?.accent || "#e07c4f");
  const [customBg, setCustomBg] = useState(customTheme?.bg || "#f5f5f0");
  const [ttsEnabled, setTtsEnabled] = useState(() => localStorage.getItem("omni_tts") === "true");
  const [voicePref, setVoicePref] = useState(() => localStorage.getItem("omni_voice") || "sage");
  const [wakeWordEnabled, setWakeWordEnabled] = useState(() => localStorage.getItem("omni_wakeword") === "true");
  const [languages, setLanguages] = useState([]);
  const [reminderHour, setReminderHour] = useState(9);
  const [reminderMinute, setReminderMinute] = useState(0);
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [savedSessions, setSavedSessions] = useState([]);
  const [aiPrefs, setAiPrefs] = useState(null);
  const [backups, setBackups] = useState([]);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [memoryCount, setMemoryCount] = useState(0);
  const [biometricCreds, setBiometricCreds] = useState([]);
  const [biometricRegistering, setBiometricRegistering] = useState(false);
  const [groupPrivacy, setGroupPrivacy] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/settings/languages`).then(r => setLanguages(r.data.languages)).catch(() => {});
    axios.get(`${API}/mood/reminder`, { headers }).then(r => {
      const rem = r.data.reminder;
      setReminderHour(rem.hour);
      setReminderMinute(rem.minute);
      setReminderEnabled(rem.enabled);
    }).catch(() => {});
    fetchSavedSessions();
    fetchAiPrefs();
    fetchBackups();
    fetchMemoryCount();
    fetchBiometricCredentials();
    axios.get(`${API}/group/my-privacy`, { headers }).then(r => setGroupPrivacy(r.data)).catch(() => {});
  }, []);

  const [exporting, setExporting] = useState(null); // "json" | "pdf" | null

  const downloadExport = async (format) => {
    setExporting(format);
    try {
      const res = await axios.get(`${API}/export/${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const ext = format === "pdf" ? "pdf" : "json";
      a.download = `omni_export_${user?.username || "user"}_${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} export downloaded`);
    } catch {
      toast.error(`Export failed. Please try again.`);
    }
    setExporting(null);
  };

  const VOICES = [
    { key: "sage", label: "Sage", desc: "Wise, measured" },
    { key: "nova", label: "Nova", desc: "Energetic, upbeat" },
    { key: "shimmer", label: "Shimmer", desc: "Bright, cheerful" },
    { key: "coral", label: "Coral", desc: "Warm, friendly" },
    { key: "echo", label: "Echo", desc: "Smooth, calm" },
    { key: "fable", label: "Fable", desc: "Expressive" },
    { key: "onyx", label: "Onyx", desc: "Deep, bold" },
    { key: "alloy", label: "Alloy", desc: "Neutral" },
  ];

  const toggleTtsSetting = () => {
    const next = !ttsEnabled;
    setTtsEnabled(next);
    localStorage.setItem("omni_tts", String(next));
  };

  const selectVoice = (v) => {
    setVoicePref(v);
    localStorage.setItem("omni_voice", v);
  };

  const changeLanguage = async (code) => {
    setLang(code);
    try {
      await axios.put(`${API}/settings/language`, { language: code }, { headers });
      toast.success(t("common.success"));
    } catch (err) { toast.error("Failed"); }
  };

  const saveReminder = async () => {
    try {
      await axios.put(`${API}/mood/reminder`, {
        hour: reminderHour, minute: reminderMinute, enabled: reminderEnabled
      }, { headers });
      toast.success(t("common.success"));
    } catch (err) { toast.error("Failed"); }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/profile`, { email: email || undefined, preferred_name: name }, { headers });
      updateUser({ email, persona: { ...user?.persona, preferred_name: name } });
      toast.success("Profile updated");
    } catch (err) { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const applyTheme = async (themeKey) => {
    setTheme(themeKey);
    try {
      await axios.put(`${API}/settings/profile`, { theme: themeKey }, { headers });
    } catch (err) { /* silent - localStorage is already set */ }
  };

  const applyHighContrast = async () => {
    toggleHighContrast();
    try {
      await axios.put(`${API}/settings/profile`, { high_contrast: !highContrast }, { headers });
    } catch (err) { /* silent */ }
  };

  const applyReduceMotion = async () => {
    toggleReduceMotion();
    try {
      await axios.put(`${API}/settings/profile`, { reduce_motion: !reduceMotion }, { headers });
    } catch (err) { /* silent */ }
  };

  // Convert hex to HSL string (without hsl() wrapper, just "H S% L%")
  const hexToHSL = (hex) => {
    let r = parseInt(hex.slice(1, 3), 16) / 255;
    let g = parseInt(hex.slice(3, 5), 16) / 255;
    let b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      else if (max === g) h = ((b - r) / d + 2) / 6;
      else h = ((r - g) / d + 4) / 6;
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };

  const lighten = (hex, amount) => {
    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);
    r = Math.min(255, r + Math.round((255 - r) * amount));
    g = Math.min(255, g + Math.round((255 - g) * amount));
    b = Math.min(255, b + Math.round((255 - b) * amount));
    return `#${r.toString(16).padStart(2,"0")}${g.toString(16).padStart(2,"0")}${b.toString(16).padStart(2,"0")}`;
  };

  const darken = (hex, amount) => {
    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);
    r = Math.max(0, Math.round(r * (1 - amount)));
    g = Math.max(0, Math.round(g * (1 - amount)));
    b = Math.max(0, Math.round(b * (1 - amount)));
    return `#${r.toString(16).padStart(2,"0")}${g.toString(16).padStart(2,"0")}${b.toString(16).padStart(2,"0")}`;
  };

  const applyCustomTheme = () => {
    const ct = {
      primary: customPrimary,
      accent: customAccent,
      bg: customBg,
      primaryHSL: hexToHSL(customPrimary),
      accentHSL: hexToHSL(customAccent),
      bgHSL: hexToHSL(customBg),
      fgHSL: hexToHSL(darken(customBg, 0.85)),
      cardHSL: hexToHSL(lighten(customBg, 0.3)),
      mutedHSL: hexToHSL(darken(customBg, 0.05)),
      mutedFgHSL: hexToHSL(darken(customBg, 0.5)),
      borderHSL: hexToHSL(darken(customBg, 0.12)),
    };
    setCustomTheme(ct);
    setTheme("custom");
    setShowCustomCreator(false);
    toast.success("Custom theme applied!");
  };

  const applyFont = async (fontKey) => {
    setFont(fontKey);
    try {
      await axios.put(`${API}/settings/profile`, { font: fontKey }, { headers });
    } catch (err) { /* silent */ }
  };

  const applyFontSize = async (sizeKey) => {
    setFontSize(sizeKey);
    try {
      await axios.put(`${API}/settings/profile`, { font_size: sizeKey }, { headers });
    } catch (err) { /* silent */ }
  };

  const fetchSavedSessions = async () => {
    try {
      const { data } = await axios.get(`${API}/sessions`, { headers });
      setSavedSessions(data.sessions || []);
    } catch { /* silent */ }
  };

  const deleteSavedSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/sessions/${sessionId}`, { headers });
      setSavedSessions(prev => prev.filter(s => s.id !== sessionId));
      toast.success("Session record deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const fetchAiPrefs = async () => {
    try {
      const { data } = await axios.get(`${API}/settings/ai-preferences`, { headers });
      setAiPrefs(data);
    } catch (err) { console.error("Failed to fetch AI prefs", err); }
  };

  const updateAiPref = async (key, value) => {
    setAiPrefs(prev => ({ ...prev, [key]: value }));
    try {
      await axios.put(`${API}/settings/ai-preferences`, { [key]: value }, { headers });
    } catch (err) { toast.error("Failed to save"); }
  };

  const fetchBackups = async () => {
    try {
      const { data } = await axios.get(`${API}/backup/list`, { headers });
      setBackups(data.backups || []);
    } catch (err) { console.error("Failed to fetch backups", err); }
  };

  const createBackup = async () => {
    setCreatingBackup(true);
    try {
      await axios.post(`${API}/backup/create`, {}, { headers });
      toast.success("Backup created successfully");
      fetchBackups();
    } catch (err) { toast.error("Failed to create backup"); }
    finally { setCreatingBackup(false); }
  };

  const deleteBackup = async (id) => {
    try {
      await axios.delete(`${API}/backup/${id}`, { headers });
      setBackups(prev => prev.filter(b => b.id !== id));
      toast.success("Backup deleted");
    } catch (err) { toast.error("Failed to delete"); }
  };

  const downloadBackup = async (id) => {
    try {
      const { data } = await axios.get(`${API}/backup/${id}/download`, { headers });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `omni_backup_${id.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Download failed"); }
  };

  const fetchMemoryCount = async () => {
    try {
      const { data } = await axios.get(`${API}/memories`, { headers });
      setMemoryCount(data.total || 0);
    } catch (err) { console.error(err); }
  };

  const deleteHistory = async () => {
    try {
      const { data } = await axios.delete(`${API}/settings/history`, { headers });
      toast.success(data.total > 0 ? `Deleted ${data.total} history items. Your account stays active.` : "No history found to delete.");
      setShowDeleteHistory(false);
    } catch (err) { toast.error("Failed to delete history"); }
  };

  const deleteAccount = async () => {
    try {
      await axios.delete(`${API}/settings/account`, { headers });
      toast.success("Account deleted");
      logout();
      navigate("/");
    } catch (err) { toast.error("Failed"); }
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep full access until the end of your paid period, with no further charges.")) return;
    try {
      const { data } = await axios.post(`${API}/subscription/cancel`, {}, { headers });
      toast.success(data.message || "Subscription cancelled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No active subscription to cancel");
    }
  };

  const [scConsent, setScConsent] = useState(null);
  const fetchScConsent = async () => {
    try {
      const { data } = await axios.get(`${API}/consent/status`, { headers });
      setScConsent(data.special_category?.granted === true);
    } catch (err) { console.error(err); }
  };
  const setSpecialConsent = async (granted) => {
    if (!granted && !window.confirm("Withdraw consent? Mood journal, insights and therapy paths will pause until you consent again. Your existing data stays until you delete it or your account.")) return;
    try {
      await axios.post(`${API}/consent/special-category`, { granted }, { headers });
      setScConsent(granted);
      toast.success(granted ? "Consent granted" : "Consent withdrawn");
    } catch (err) { toast.error("Failed to update consent"); }
  };
  useEffect(() => { fetchScConsent(); }, []);

  const fetchBiometricCredentials = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/webauthn/credentials`, { headers });
      setBiometricCreds(data.credentials || []);
    } catch { /* silent */ }
  };

  const registerBiometric = async () => {
    setBiometricRegistering(true);
    try {
      const { startRegistration, browserSupportsWebAuthn } = await import("@simplewebauthn/browser");
      if (!browserSupportsWebAuthn()) {
        toast.error("Biometric login is not supported on this browser/device.");
        return;
      }
      const { data: optionsJSON } = await axios.post(`${API}/auth/webauthn/register/options`, {}, { headers });
      const regResp = await startRegistration({ optionsJSON });
      const { data } = await axios.post(`${API}/auth/webauthn/register/verify`, regResp, { headers });
      toast.success(`Biometric registered! (${data.device_name})`);
      fetchBiometricCredentials();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Registration failed";
      if (msg.includes("cancelled") || msg.includes("AbortError")) {
        toast.info("Biometric registration cancelled");
      } else {
        toast.error(msg);
      }
    } finally {
      setBiometricRegistering(false);
    }
  };

  const removeBiometricCredential = async (credId) => {
    try {
      await axios.delete(`${API}/auth/webauthn/credentials/${credId}`, { headers });
      toast.success("Biometric credential removed");
      fetchBiometricCredentials();
    } catch { toast.error("Failed to remove credential"); }
  };

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">
          <div>
            <h1 className="font-heading text-3xl font-medium mb-2">Settings</h1>
            <p className="text-muted-foreground">Manage your account and preferences.</p>
          </div>

          {/* Appearance - Theme */}
          <div className="card-omni p-8">
            <div className="flex items-center gap-3 mb-6">
              <Palette className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-xl font-medium">Appearance</h2>
            </div>

            <div className="space-y-6">
              {/* Dark Mode Toggle */}
              <div className="flex items-center justify-between p-4 rounded-2xl bg-muted/30 border border-border/30">
                <div className="flex items-center gap-3">
                  {darkMode ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-amber-500" />}
                  <div>
                    <p className="text-sm font-medium">{darkMode ? "Dark Mode" : "Light Mode"}</p>
                    <p className="text-xs text-muted-foreground">Toggle dark variant of current theme</p>
                  </div>
                </div>
                <button
                  data-testid="settings-dark-toggle"
                  onClick={toggleDarkMode}
                  className={cn(
                    "relative w-12 h-7 rounded-full transition-all",
                    darkMode ? "bg-primary" : "bg-muted"
                  )}
                >
                  <div className={cn(
                    "absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all",
                    darkMode ? "left-6" : "left-1"
                  )} />
                </button>
              </div>

              {/* High Contrast Toggle */}
              <div className="flex items-center justify-between p-4 rounded-2xl bg-muted/30 border border-border/30">
                <div className="flex items-center gap-3">
                  <Eye className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium">High Contrast</p>
                    <p className="text-xs text-muted-foreground">Maximum text-to-background contrast for accessibility</p>
                  </div>
                </div>
                <button
                  data-testid="settings-contrast-toggle"
                  onClick={applyHighContrast}
                  className={cn(
                    "relative w-12 h-7 rounded-full transition-all",
                    highContrast ? "bg-primary" : "bg-muted"
                  )}
                >
                  <div className={cn(
                    "absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all",
                    highContrast ? "left-6" : "left-1"
                  )} />
                </button>
              </div>

              {/* Reduce Motion Toggle */}
              <div className="flex items-center justify-between p-4 rounded-2xl bg-muted/30 border border-border/30">
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium">Reduce Motion</p>
                    <p className="text-xs text-muted-foreground">Disable animations for motion sensitivity</p>
                  </div>
                </div>
                <button
                  data-testid="settings-motion-toggle"
                  onClick={applyReduceMotion}
                  className={cn(
                    "relative w-12 h-7 rounded-full transition-all",
                    reduceMotion ? "bg-primary" : "bg-muted"
                  )}
                >
                  <div className={cn(
                    "absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all",
                    reduceMotion ? "left-6" : "left-1"
                  )} />
                </button>
              </div>

              <div>
                <label className="text-sm font-medium mb-3 block">Theme</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {THEMES.map(t => {
                    const c = t.colors;
                    const isDark = darkMode;
                    const bg = isDark ? c.darkBg : c.bg;
                    const fg = isDark ? c.darkFg : c.fg;
                    const primary = isDark ? c.darkPrimary : c.primary;
                    const card = isDark ? c.darkCard : c.card;
                    const muted = isDark ? (c.darkCard) : c.muted;
                    const selected = theme === t.key;
                    return (
                      <button
                        key={t.key}
                        data-testid={`theme-${t.key}`}
                        onClick={() => applyTheme(t.key)}
                        className={cn(
                          "relative rounded-2xl border-2 text-left transition-all overflow-hidden",
                          selected ? "border-primary ring-2 ring-primary/20" : "border-border/50 hover:border-primary/40"
                        )}
                      >
                        {/* Mini preview mockup */}
                        <div className="p-3" style={{ backgroundColor: bg }}>
                          <div className="rounded-lg p-2.5 space-y-2" style={{ backgroundColor: card }}>
                            {/* Mini header */}
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: primary }}>
                                <MessageSquare className="w-3 h-3" style={{ color: "#fff" }} />
                              </div>
                              <div className="h-2 rounded-full flex-1" style={{ backgroundColor: fg, opacity: 0.7 }} />
                            </div>
                            {/* Mini chat bubbles */}
                            <div className="flex flex-col gap-1.5">
                              <div className="self-start rounded-lg px-2 py-1" style={{ backgroundColor: muted }}>
                                <div className="h-1.5 w-16 rounded-full" style={{ backgroundColor: fg, opacity: 0.4 }} />
                              </div>
                              <div className="self-end rounded-lg px-2 py-1" style={{ backgroundColor: primary }}>
                                <div className="h-1.5 w-12 rounded-full" style={{ backgroundColor: "#fff", opacity: 0.8 }} />
                              </div>
                            </div>
                            {/* Mini input */}
                            <div className="rounded-md h-3 border" style={{ borderColor: fg + "22", backgroundColor: bg }} />
                          </div>
                        </div>
                        {/* Label */}
                        <div className="px-3 py-2.5 flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: primary }} />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">{t.name}</p>
                            <p className="text-[10px] text-muted-foreground ui-label">{t.vibe}</p>
                          </div>
                          {selected && (
                            <div className="ml-auto w-5 h-5 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                              <Check className="w-3 h-3 text-primary-foreground" />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Custom Theme Creator */}
                {!showCustomCreator ? (
                  <button
                    data-testid="open-theme-creator"
                    onClick={() => setShowCustomCreator(true)}
                    className={cn(
                      "w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed text-left transition-all hover:border-primary/40",
                      theme === "custom" ? "border-primary bg-primary/5" : "border-border/50"
                    )}
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary via-accent to-muted flex items-center justify-center">
                      <Plus className="w-4 h-4 text-primary-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{theme === "custom" ? "Edit Custom Theme" : "Create Custom Theme"}</p>
                      <p className="text-[10px] text-muted-foreground ui-label">Pick your own colors</p>
                    </div>
                    {theme === "custom" && (
                      <div className="ml-auto w-5 h-5 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-primary-foreground" />
                      </div>
                    )}
                  </button>
                ) : (
                  <div data-testid="theme-creator-panel" className="p-5 rounded-2xl border-2 border-primary/30 bg-primary/5 space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">Theme Creator</p>
                      <button data-testid="cancel-theme-creator" onClick={() => setShowCustomCreator(false)} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                    </div>

                    {/* Color Pickers */}
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      <div>
                        <label className="text-xs font-medium mb-1.5 block">Primary</label>
                        <div className="relative">
                          <input
                            data-testid="custom-primary-color"
                            type="color"
                            value={customPrimary}
                            onChange={e => setCustomPrimary(e.target.value)}
                            className="w-full h-10 rounded-xl cursor-pointer border border-border/50"
                          />
                          <p className="text-[10px] text-muted-foreground mt-1 text-center ui-label font-mono">{customPrimary}</p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1.5 block">Accent</label>
                        <div className="relative">
                          <input
                            data-testid="custom-accent-color"
                            type="color"
                            value={customAccent}
                            onChange={e => setCustomAccent(e.target.value)}
                            className="w-full h-10 rounded-xl cursor-pointer border border-border/50"
                          />
                          <p className="text-[10px] text-muted-foreground mt-1 text-center ui-label font-mono">{customAccent}</p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium mb-1.5 block">Background</label>
                        <div className="relative">
                          <input
                            data-testid="custom-bg-color"
                            type="color"
                            value={customBg}
                            onChange={e => setCustomBg(e.target.value)}
                            className="w-full h-10 rounded-xl cursor-pointer border border-border/50"
                          />
                          <p className="text-[10px] text-muted-foreground mt-1 text-center ui-label font-mono">{customBg}</p>
                        </div>
                      </div>
                    </div>

                    {/* Live Preview */}
                    <div className="rounded-lg p-3 space-y-2" style={{ backgroundColor: customBg }}>
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: customPrimary }}>
                          <MessageSquare className="w-3 h-3" style={{ color: "#fff" }} />
                        </div>
                        <div className="h-2 rounded-full flex-1" style={{ backgroundColor: darken(customBg, 0.85), opacity: 0.7 }} />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <div className="self-start rounded-lg px-2 py-1" style={{ backgroundColor: darken(customBg, 0.05) }}>
                          <div className="h-1.5 w-16 rounded-full" style={{ backgroundColor: darken(customBg, 0.85), opacity: 0.4 }} />
                        </div>
                        <div className="self-end rounded-lg px-2 py-1" style={{ backgroundColor: customPrimary }}>
                          <div className="h-1.5 w-12 rounded-full" style={{ backgroundColor: "#fff", opacity: 0.8 }} />
                        </div>
                        <div className="self-start rounded-lg px-2 py-1" style={{ backgroundColor: customAccent }}>
                          <div className="h-1.5 w-10 rounded-full" style={{ backgroundColor: "#fff", opacity: 0.8 }} />
                        </div>
                      </div>
                    </div>

                    <Button
                      data-testid="apply-custom-theme"
                      onClick={applyCustomTheme}
                      className="w-full btn-omni bg-primary text-primary-foreground"
                    >
                      Apply Custom Theme
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium mb-3 block flex items-center gap-2">
                  <Type className="w-4 h-4" strokeWidth={1.5} />
                  Font
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {FONTS.map(f => (
                    <button
                      key={f.key}
                      data-testid={`font-${f.key}`}
                      onClick={() => applyFont(f.key)}
                      className={cn(
                        "relative p-4 rounded-2xl border-2 text-left transition-all",
                        font === f.key
                          ? "border-primary bg-primary/5"
                          : "border-border/50 hover:border-primary/30"
                      )}
                    >
                      <p className={`text-lg font-semibold mb-1 ${f.sample}`}>{f.name}</p>
                      <p className="text-xs text-muted-foreground">{f.vibe}</p>
                      {font === f.key && (
                        <div className="absolute top-3 right-3 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-3 block flex items-center gap-2">
                  <ALargeSmall className="w-4 h-4" strokeWidth={1.5} />
                  Text Size
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {FONT_SIZES.map(s => (
                    <button
                      key={s.key}
                      data-testid={`fontsize-${s.key}`}
                      onClick={() => applyFontSize(s.key)}
                      className={cn(
                        "relative p-4 rounded-2xl border-2 text-center transition-all",
                        fontSize === s.key
                          ? "border-primary bg-primary/5"
                          : "border-border/50 hover:border-primary/30"
                      )}
                    >
                      <p className="font-semibold mb-0.5" style={{ fontSize: s.base }}>{s.name}</p>
                      <p className="text-[10px] text-muted-foreground ui-label">{s.desc}</p>
                      {fontSize === s.key && (
                        <div className="absolute top-2 right-2 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Voice */}
          <div className="card-omni p-8">
            <div className="flex items-center gap-3 mb-6">
              <Volume2 className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-xl font-medium">Voice</h2>
            </div>
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Auto-read AI replies</p>
                  <p className="text-xs text-muted-foreground">Omni will speak responses aloud</p>
                </div>
                <button
                  data-testid="tts-settings-toggle"
                  onClick={toggleTtsSetting}
                  className={cn(
                    "w-12 h-7 rounded-full transition-all relative",
                    ttsEnabled ? "bg-primary" : "bg-muted"
                  )}
                >
                  <div className={cn(
                    "w-5 h-5 bg-white rounded-full absolute top-1 transition-all shadow-sm",
                    ttsEnabled ? "left-6" : "left-1"
                  )} />
                </button>
              </div>

              <div>
                <label className="text-sm font-medium mb-3 block">Omni&apos;s Voice</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {VOICES.map(v => (
                    <button
                      key={v.key}
                      data-testid={`settings-voice-${v.key}`}
                      onClick={() => selectVoice(v.key)}
                      className={cn(
                        "relative p-3 rounded-2xl border-2 text-left transition-all",
                        voicePref === v.key
                          ? "border-primary bg-primary/5"
                          : "border-border/50 hover:border-primary/30"
                      )}
                    >
                      <p className="text-sm font-semibold">{v.label}</p>
                      <p className="text-[10px] text-muted-foreground ui-label">{v.desc}</p>
                      {voicePref === v.key && (
                        <div className="absolute top-2 right-2 w-4 h-4 bg-primary rounded-full flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-primary-foreground" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border/30">
                <div>
                  <p className="text-sm font-medium">Wake Word</p>
                  <p className="text-xs text-muted-foreground">Say &quot;Hey Omni&quot; to start voice input on chat page</p>
                </div>
                <button
                  data-testid="wake-word-settings-toggle"
                  onClick={() => {
                    const next = !wakeWordEnabled;
                    setWakeWordEnabled(next);
                    localStorage.setItem("omni_wakeword", String(next));
                  }}
                  className={cn(
                    "w-12 h-7 rounded-full transition-all relative",
                    wakeWordEnabled ? "bg-primary" : "bg-muted"
                  )}
                >
                  <div className={cn(
                    "w-5 h-5 bg-white rounded-full absolute top-1 transition-all shadow-sm",
                    wakeWordEnabled ? "left-6" : "left-1"
                  )} />
                </button>
              </div>
            </div>
          </div>

          {/* Language */}
          <div className="card-omni p-8">
            <div className="flex items-center gap-3 mb-6">
              <Globe className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-xl font-medium">{t("settings.language")}</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{t("settings.languageDesc")}</p>
            <select
              data-testid="language-select"
              value={lang}
              onChange={e => changeLanguage(e.target.value)}
              className="input-omni w-full border border-input h-12 bg-background appearance-none cursor-pointer"
            >
              {languages.map(l => (
                <option key={l.code} value={l.code}>
                  {`${l.flag} ${l.native} (${l.name})`}
                </option>
              ))}
            </select>
          </div>

          {/* Mood Reminder */}
          <div className="card-omni p-8">
            <div className="flex items-center gap-3 mb-6">
              <Bell className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-xl font-medium">{t("mood.reminder")}</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{t("mood.reminderDesc")}</p>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{t("mood.reminder")}</p>
                <button
                  data-testid="reminder-toggle"
                  onClick={() => { setReminderEnabled(!reminderEnabled); }}
                  className={cn(
                    "w-12 h-7 rounded-full transition-all relative",
                    reminderEnabled ? "bg-primary" : "bg-muted"
                  )}
                >
                  <div className={cn(
                    "w-5 h-5 bg-white rounded-full absolute top-1 transition-all shadow-sm",
                    reminderEnabled ? "left-6" : "left-1"
                  )} />
                </button>
              </div>

              {reminderEnabled && (
                <div className="flex items-center gap-3">
                  <label className="text-sm text-muted-foreground">{t("mood.reminderTime")}:</label>
                  <select
                    data-testid="reminder-hour"
                    value={reminderHour}
                    onChange={e => setReminderHour(Number(e.target.value))}
                    className="input-omni border border-input h-10 bg-background w-20"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>{String(i).padStart(2, "0")}</option>
                    ))}
                  </select>
                  <span>:</span>
                  <select
                    data-testid="reminder-minute"
                    value={reminderMinute}
                    onChange={e => setReminderMinute(Number(e.target.value))}
                    className="input-omni border border-input h-10 bg-background w-20"
                  >
                    {[0, 15, 30, 45].map(m => (
                      <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                    ))}
                  </select>
                  <Button
                    data-testid="save-reminder-btn"
                    onClick={saveReminder}
                    className="btn-omni bg-primary text-primary-foreground ml-auto"
                  >
                    {t("common.save")}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Push Notifications */}
          <div className="card-omni p-8" data-testid="push-notifications-section">
            <div className="flex items-center gap-3 mb-4">
              <Smartphone className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <div>
                <h2 className="font-heading text-xl font-medium">Push Notifications</h2>
                <p className="text-xs text-muted-foreground">Get notified when you're added to a group or receive a digest</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Enable Push Notifications</p>
                <button
                  data-testid="push-enable-btn"
                  onClick={async () => {
                    const { registerPushToken: reg } = await import("@/lib/pushNotifications");
                    const ok = await reg(token);
                    if (ok) alert("Push notifications enabled!");
                    else alert("Could not enable push notifications. Check browser permissions or Firebase configuration.");
                  }}
                  className="px-4 py-2 rounded-xl text-xs font-medium bg-primary text-primary-foreground hover:opacity-90 transition-all"
                >
                  Enable
                </button>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Push notifications require your browser's permission and Firebase Cloud Messaging to be configured. Notifications include: group member additions, weekly activity digests, and admin announcements.
              </p>
            </div>
          </div>

          {/* Profile */}
          <div className="card-omni p-8 space-y-5">
            <div className="flex items-center gap-3 mb-2">
              <User className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-xl font-medium">Profile</h2>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Username</label>
              <input className="input-omni w-full border border-input bg-muted/50" value={user?.username || ""} disabled />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Preferred Name</label>
              <input
                data-testid="settings-name-input"
                className="input-omni w-full border border-input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="What should Omni call you?"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Email <span className="text-muted-foreground font-normal">(optional)</span></label>
              <input
                data-testid="settings-email-input"
                type="email"
                className="input-omni w-full border border-input"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="For account recovery only"
              />
            </div>

            <Button data-testid="save-profile-btn" onClick={saveProfile} disabled={saving} className="btn-omni bg-primary text-primary-foreground">
              {saving ? "..." : "Save Changes"}
            </Button>
          </div>

          {/* AI Intelligence */}
          <div className="card-omni p-8">
            <div className="flex items-center gap-3 mb-6">
              <Brain className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <div>
                <h2 className="font-heading text-xl font-medium">AI Intelligence</h2>
                <p className="text-xs text-muted-foreground">How Omni communicates with you</p>
              </div>
            </div>
            {aiPrefs && (
              <div className="space-y-6">
                {/* Communication Style */}
                <div>
                  <label className="text-sm font-medium mb-3 block flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
                    Communication Style
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[
                      { key: "adaptive", label: "Adaptive", desc: "Mirrors your energy" },
                      { key: "casual", label: "Casual", desc: "Like texting a friend" },
                      { key: "formal", label: "Formal", desc: "Professional tone" },
                      { key: "poetic", label: "Poetic", desc: "Literary, expressive" },
                      { key: "direct", label: "Direct", desc: "Straight to the point" },
                    ].map(s => (
                      <button
                        key={s.key}
                        data-testid={`ai-style-${s.key}`}
                        onClick={() => updateAiPref("communication_style", s.key)}
                        className={cn(
                          "text-left p-3 rounded-2xl border-2 transition-all",
                          aiPrefs.communication_style === s.key
                            ? "border-primary bg-primary/5"
                            : "border-border/50 hover:border-primary/30"
                        )}
                      >
                        <p className="text-sm font-semibold">{s.label}</p>
                        <p className="text-[10px] text-muted-foreground">{s.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Response Length */}
                <div>
                  <label className="text-sm font-medium mb-3 block flex items-center gap-2">
                    <Gauge className="w-4 h-4" strokeWidth={1.5} />
                    Response Length
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "brief", label: "Brief", desc: "2-4 sentences" },
                      { key: "medium", label: "Medium", desc: "Balanced depth" },
                      { key: "detailed", label: "Detailed", desc: "Thorough explanations" },
                    ].map(l => (
                      <button
                        key={l.key}
                        data-testid={`ai-length-${l.key}`}
                        onClick={() => updateAiPref("response_length", l.key)}
                        className={cn(
                          "text-center p-3 rounded-2xl border-2 transition-all",
                          aiPrefs.response_length === l.key
                            ? "border-primary bg-primary/5"
                            : "border-border/50 hover:border-primary/30"
                        )}
                      >
                        <p className="text-sm font-semibold">{l.label}</p>
                        <p className="text-[10px] text-muted-foreground">{l.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Emotional Sensitivity */}
                <div>
                  <label className="text-sm font-medium mb-3 block flex items-center gap-2">
                    <Heart className="w-4 h-4" strokeWidth={1.5} />
                    Emotional Sensitivity
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "low", label: "Practical", desc: "Focus on solutions" },
                      { key: "medium", label: "Balanced", desc: "Empathy + advice" },
                      { key: "high", label: "High Empathy", desc: "Feelings-first" },
                    ].map(s => (
                      <button
                        key={s.key}
                        data-testid={`ai-sensitivity-${s.key}`}
                        onClick={() => updateAiPref("emotional_sensitivity", s.key)}
                        className={cn(
                          "text-center p-3 rounded-2xl border-2 transition-all",
                          aiPrefs.emotional_sensitivity === s.key
                            ? "border-primary bg-primary/5"
                            : "border-border/50 hover:border-primary/30"
                        )}
                      >
                        <p className="text-sm font-semibold">{s.label}</p>
                        <p className="text-[10px] text-muted-foreground">{s.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Humor Level */}
                <div>
                  <label className="text-sm font-medium mb-3 block">Humor Level</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "none", label: "Serious", desc: "No humor" },
                      { key: "moderate", label: "Moderate", desc: "Occasional wit" },
                      { key: "high", label: "Witty", desc: "Frequent humor" },
                    ].map(h => (
                      <button
                        key={h.key}
                        data-testid={`ai-humor-${h.key}`}
                        onClick={() => updateAiPref("humor_level", h.key)}
                        className={cn(
                          "text-center p-3 rounded-2xl border-2 transition-all",
                          aiPrefs.humor_level === h.key
                            ? "border-primary bg-primary/5"
                            : "border-border/50 hover:border-primary/30"
                        )}
                      >
                        <p className="text-sm font-semibold">{h.label}</p>
                        <p className="text-[10px] text-muted-foreground">{h.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Cultural Awareness Mode */}
                <div className="border-t border-border/30 pt-6">
                  <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                    <Earth className="w-4 h-4" strokeWidth={1.5} />
                    Cultural Awareness
                  </label>
                  <p className="text-xs text-muted-foreground mb-4">
                    Choose how deeply Omni adapts to your cultural context. Ultra mode makes every interaction deeply culturally personalized.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      {
                        key: "standard",
                        label: "Standard",
                        desc: "Universally respectful and inclusive",
                        icon: "🌍",
                      },
                      {
                        key: "ultra",
                        label: "Ultra Cultural",
                        desc: "Deep heritage-aware, greeting-aware, tradition-sensitive responses",
                        icon: "✦",
                      },
                    ].map(m => (
                      <button
                        key={m.key}
                        data-testid={`cultural-mode-${m.key}`}
                        onClick={() => updateAiPref("cultural_mode", m.key)}
                        className={cn(
                          "text-left p-4 rounded-2xl border-2 transition-all",
                          (aiPrefs.cultural_mode || "standard") === m.key
                            ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                            : "border-border/50 hover:border-primary/30"
                        )}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-base">{m.icon}</span>
                          <p className="text-sm font-semibold">{m.label}</p>
                          {(aiPrefs.cultural_mode || "standard") === m.key && (
                            <span className="ml-auto w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                              <Check className="w-2.5 h-2.5 text-primary-foreground" />
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground leading-relaxed">{m.desc}</p>
                      </button>
                    ))}
                  </div>

                  {(aiPrefs.cultural_mode === "ultra") && (
                    <div data-testid="cultural-profile-section" className="mt-4 p-4 rounded-2xl bg-primary/5 border border-primary/20 space-y-3 animate-in fade-in">
                      <p className="text-xs font-semibold flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-primary" /> Your Cultural Profile
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        Share as much or as little as you like. This helps Omni greet you properly, reference your traditions, and avoid cultural blind spots.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-medium mb-0.5 block">Region / Cultural Heritage</label>
                          <select
                            data-testid="cultural-region-select"
                            value={aiPrefs.cultural_region || ""}
                            onChange={e => updateAiPref("cultural_region", e.target.value)}
                            className="w-full text-xs px-3 py-2 rounded-xl border border-border/50 bg-card"
                          >
                            <option value="">Not specified</option>
                            <option value="west_africa">West Africa</option>
                            <option value="east_africa">East Africa</option>
                            <option value="north_africa">North Africa</option>
                            <option value="southern_africa">Southern Africa</option>
                            <option value="south_asia">South Asia</option>
                            <option value="east_asia">East Asia</option>
                            <option value="southeast_asia">Southeast Asia</option>
                            <option value="central_asia">Central Asia</option>
                            <option value="middle_east">Middle East</option>
                            <option value="western_europe">Western Europe</option>
                            <option value="eastern_europe">Eastern Europe</option>
                            <option value="northern_europe">Northern Europe</option>
                            <option value="mediterranean">Mediterranean</option>
                            <option value="north_america">North America</option>
                            <option value="latin_america">Latin America / Caribbean</option>
                            <option value="oceania">Oceania / Pacific Islands</option>
                            <option value="indigenous">Indigenous / First Nations</option>
                            <option value="diaspora">Diaspora / Multicultural</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-medium mb-0.5 block">Values Emphasis</label>
                          <select
                            data-testid="cultural-values-select"
                            value={aiPrefs.cultural_values || ""}
                            onChange={e => updateAiPref("cultural_values", e.target.value)}
                            className="w-full text-xs px-3 py-2 rounded-xl border border-border/50 bg-card"
                          >
                            <option value="">Balanced</option>
                            <option value="collectivist">Community-first (family, group harmony)</option>
                            <option value="individualist">Individual-first (autonomy, self-expression)</option>
                            <option value="spiritual">Spiritually-guided (faith-centered decisions)</option>
                            <option value="pragmatic">Practical (results-focused, adaptable)</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-medium mb-0.5 block">Greeting Preference</label>
                          <select
                            data-testid="cultural-greeting-select"
                            value={aiPrefs.greeting_style || ""}
                            onChange={e => updateAiPref("greeting_style", e.target.value)}
                            className="w-full text-xs px-3 py-2 rounded-xl border border-border/50 bg-card"
                          >
                            <option value="">Natural / adaptive</option>
                            <option value="cultural">Culturally traditional greeting</option>
                            <option value="formal">Formal / respectful</option>
                            <option value="warm">Warm and familiar</option>
                            <option value="minimal">Minimal / straight to business</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-medium mb-0.5 block">Dietary / Lifestyle Awareness</label>
                          <select
                            data-testid="cultural-dietary-select"
                            value={aiPrefs.dietary_awareness || ""}
                            onChange={e => updateAiPref("dietary_awareness", e.target.value)}
                            className="w-full text-xs px-3 py-2 rounded-xl border border-border/50 bg-card"
                          >
                            <option value="">No preference</option>
                            <option value="halal">Halal-conscious</option>
                            <option value="kosher">Kosher-conscious</option>
                            <option value="vegetarian">Vegetarian</option>
                            <option value="vegan">Vegan</option>
                            <option value="hindu_veg">Hindu vegetarian</option>
                            <option value="none">No restrictions</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Memory Info */}
                <div className="flex items-center justify-between p-4 rounded-2xl bg-muted/30 border border-border/30">
                  <div className="flex items-center gap-3">
                    <Brain className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-sm font-medium">Memory</p>
                      <p className="text-xs text-muted-foreground">
                        {memoryCount > 0 ? `Omni remembers ${memoryCount} things about you` : "Omni will learn about you as you chat"}
                      </p>
                    </div>
                  </div>
                  <Button
                    data-testid="view-memories-btn"
                    onClick={() => navigate("/chat")}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs"
                  >
                    View in Chat
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Backup & Data */}
          <div className="card-omni p-8">
            <div className="flex items-center gap-3 mb-6">
              <HardDrive className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <div>
                <h2 className="font-heading text-xl font-medium">Backup & Data</h2>
                <p className="text-xs text-muted-foreground">Control your data and backups</p>
              </div>
            </div>
            <div className="space-y-5">
              {/* Backup Schedule */}
              {aiPrefs && (
                <div>
                  <label className="text-sm font-medium mb-3 block flex items-center gap-2">
                    <Clock className="w-4 h-4" strokeWidth={1.5} />
                    Auto-Backup Schedule
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { key: "manual", label: "Manual", desc: "You decide" },
                      { key: "daily", label: "Daily", desc: "Every day" },
                      { key: "weekly", label: "Weekly", desc: "Every week" },
                      { key: "monthly", label: "Monthly", desc: "Every month" },
                    ].map(f => (
                      <button
                        key={f.key}
                        data-testid={`backup-freq-${f.key}`}
                        onClick={() => updateAiPref("backup_frequency", f.key)}
                        className={cn(
                          "text-center p-3 rounded-2xl border-2 transition-all",
                          aiPrefs.backup_frequency === f.key
                            ? "border-primary bg-primary/5"
                            : "border-border/50 hover:border-primary/30"
                        )}
                      >
                        <p className="text-sm font-semibold">{f.label}</p>
                        <p className="text-[10px] text-muted-foreground">{f.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Create Backup */}
              <div className="flex items-center justify-between p-4 rounded-2xl bg-muted/30 border border-border/30">
                <div>
                  <p className="text-sm font-medium">Manual Backup</p>
                  <p className="text-xs text-muted-foreground">Create a full backup of all your data now</p>
                </div>
                <Button
                  data-testid="create-backup-btn"
                  onClick={createBackup}
                  disabled={creatingBackup}
                  className="btn-omni bg-primary text-primary-foreground rounded-full gap-2"
                >
                  <Save className="w-3.5 h-3.5" />
                  {creatingBackup ? "Creating..." : "Backup Now"}
                </Button>
              </div>

              {/* Existing Backups */}
              {backups.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-3">Your Backups</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {backups.map(b => (
                      <div key={b.id} data-testid={`backup-${b.id}`} className="flex items-center gap-3 p-3 rounded-xl bg-muted/20 border border-border/30">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium">
                            {b.backup_type === "manual" ? "Manual" : "Auto"} — {new Date(b.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {b.conversations_count} conversations, {b.messages_count} messages, {b.memories_count} memories
                            {b.size_bytes ? ` — ${(b.size_bytes / 1024).toFixed(0)}KB` : ""}
                          </p>
                        </div>
                        <button
                          data-testid={`download-backup-${b.id}`}
                          onClick={() => downloadBackup(b.id)}
                          className="p-2 rounded-xl hover:bg-primary/10 text-muted-foreground hover:text-primary transition-all"
                          title="Download"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`delete-backup-${b.id}`}
                          onClick={() => deleteBackup(b.id)}
                          className="p-2 rounded-xl hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Privacy */}
          <div className="card-omni p-8" data-testid="export-my-data-section">
            <div className="flex items-center gap-3 mb-6">
              <Download className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <div>
                <h2 className="font-heading text-xl font-medium">Export My Data</h2>
                <p className="text-xs text-muted-foreground">Download all your conversations, mood logs, and goals</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center justify-between p-4 rounded-2xl bg-muted/30 border border-border/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
                    <FileJson className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">JSON Export</p>
                    <p className="text-[10px] text-muted-foreground">Full data — conversations, moods, goals, memories, bookmarks</p>
                  </div>
                </div>
                <Button
                  data-testid="export-json-btn"
                  onClick={() => downloadExport("json")}
                  disabled={exporting === "json"}
                  className="btn-omni bg-primary text-primary-foreground rounded-full gap-2"
                  size="sm"
                >
                  {exporting === "json" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  {exporting === "json" ? "Exporting..." : "JSON"}
                </Button>
              </div>
              <div className="flex items-center justify-between p-4 rounded-2xl bg-muted/30 border border-border/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/10">
                    <FileText className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">PDF Report</p>
                    <p className="text-[10px] text-muted-foreground">Formatted report — cover page, conversations, mood journal, goals</p>
                  </div>
                </div>
                <Button
                  data-testid="export-pdf-btn"
                  onClick={() => downloadExport("pdf")}
                  disabled={exporting === "pdf"}
                  className="btn-omni bg-primary text-primary-foreground rounded-full gap-2"
                  size="sm"
                >
                  {exporting === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  {exporting === "pdf" ? "Exporting..." : "PDF"}
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground mt-3">Your data, your property. Exports include everything Omni stores about you.</p>
          </div>

          {/* Privacy - Original */}
          <div className="card-omni p-8">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-xl font-medium">Privacy</h2>
            </div>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li>Your conversations are encrypted and stored securely.</li>
              <li>We do not sell, share, or mine your personal data.</li>
              <li>Anonymous accounts have no identifying information on our servers.</li>
              <li>You can delete your entire account and all data at any time.</li>
            </ul>
          </div>

          {/* Group Privacy Self-Service */}
          {groupPrivacy?.in_group && (
            <div className="card-omni p-8" data-testid="group-privacy-section">
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-5 h-5 text-primary" strokeWidth={1.5} />
                <div>
                  <h2 className="font-heading text-xl font-medium">Group Privacy</h2>
                  <p className="text-xs text-muted-foreground">{groupPrivacy.group_name} — {groupPrivacy.role === "admin" ? "Admin" : "Member"}</p>
                </div>
              </div>

              {/* Privacy Level */}
              <div className="mb-5 p-4 rounded-xl bg-muted/30 border border-border/30">
                <div className="flex items-center gap-2 mb-2">
                  {groupPrivacy.privacy_level === "full" ? <Lock className="w-4 h-4 text-emerald-500" /> : <Shield className="w-4 h-4 text-amber-500" />}
                  <span className="text-sm font-medium">{groupPrivacy.privacy_info?.label}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{groupPrivacy.privacy_info?.description}</p>
              </div>

              {/* What admin can/cannot see */}
              {groupPrivacy.role === "member" && (
                <div className="space-y-3 mb-5">
                  {groupPrivacy.privacy_info?.admin_can_view?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Admin can see</p>
                      <div className="flex flex-wrap gap-1.5">
                        {groupPrivacy.privacy_info.admin_can_view.map(item => (
                          <span key={item} className="px-2 py-1 text-[10px] rounded-full bg-amber-500/10 text-amber-500 border border-amber-500/20">{item.replace(/_/g, " ")}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {groupPrivacy.privacy_info?.admin_cannot_view?.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Private to you</p>
                      <div className="flex flex-wrap gap-1.5">
                        {groupPrivacy.privacy_info.admin_cannot_view.map(item => (
                          <span key={item} className="px-2 py-1 text-[10px] rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">{item.replace(/_/g, " ")}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Module Access */}
              {groupPrivacy.allowed_modules && (
                <div className="mb-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Your AI Modules</p>
                  <div className="flex flex-wrap gap-1.5">
                    {groupPrivacy.allowed_modules.map(mod => (
                      <span key={mod} className="px-2.5 py-1 text-[10px] rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">
                        {groupPrivacy.all_modules?.[mod] || mod}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin link */}
              {groupPrivacy.role === "admin" && (
                <Button variant="outline" className="w-full rounded-xl gap-2 mt-2" onClick={() => navigate("/group-admin")} data-testid="go-group-admin-btn">
                  <Crown className="w-4 h-4" /> Open Group Admin Dashboard
                </Button>
              )}

              {/* Member info */}
              {groupPrivacy.role === "member" && groupPrivacy.admin_name && (
                <p className="text-xs text-muted-foreground mt-3">Group admin: <strong>{groupPrivacy.admin_name}</strong>. Contact them to change your privacy or module settings.</p>
              )}
            </div>
          )}

          {/* Biometric Login */}
          <div className="card-omni p-8">
            <div className="flex items-center gap-3 mb-4">
              <Fingerprint className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <div>
                <h2 className="font-heading text-xl font-medium">Biometric Login</h2>
                <p className="text-xs text-muted-foreground">Use fingerprint, Face ID, or Windows Hello to sign in instantly</p>
              </div>
            </div>

            {biometricCreds.length > 0 ? (
              <div className="space-y-3">
                <div className="space-y-2">
                  {biometricCreds.map(cred => (
                    <div key={cred.id} data-testid={`biometric-cred-${cred.id}`} className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border/30">
                      <div className="flex items-center gap-3">
                        <Smartphone className="w-4 h-4 text-emerald-500" />
                        <div>
                          <p className="text-sm font-medium">{cred.device_name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            Registered {new Date(cred.created_at).toLocaleDateString()}
                            {cred.last_used_at && ` · Last used ${new Date(cred.last_used_at).toLocaleDateString()}`}
                          </p>
                        </div>
                      </div>
                      <button
                        data-testid={`remove-biometric-${cred.id}`}
                        onClick={() => removeBiometricCredential(cred.id)}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <Button
                  data-testid="add-biometric-btn"
                  onClick={registerBiometric}
                  disabled={biometricRegistering}
                  variant="outline"
                  className="w-full rounded-xl gap-2"
                >
                  {biometricRegistering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {biometricRegistering ? "Registering..." : "Add Another Device"}
                </Button>
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-3">
                  <Fingerprint className="w-7 h-7 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Register your device to sign in with biometrics.<br />
                  Your biometric data never leaves your device.
                </p>
                <Button
                  data-testid="enable-biometric-btn"
                  onClick={registerBiometric}
                  disabled={biometricRegistering}
                  className="rounded-xl gap-2"
                >
                  {biometricRegistering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
                  {biometricRegistering ? "Registering..." : "Enable Biometric Login"}
                </Button>
              </div>
            )}
          </div>

          {/* Subscription */}
          <UsageMeter />
          <PushNotificationCard />
          <ReferralCard />
          <div className="card-omni p-8">
            <h2 className="font-heading text-xl font-medium mb-4">Subscription</h2>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold capitalize">{user?.tier || "Free"} Plan</p>
                <p className="text-sm text-muted-foreground">
                  {user?.tier === "free" ? "Upgrade for full access to all modules." : "All features unlocked."}
                </p>
              </div>
              <Button
                data-testid="manage-plan-btn"
                onClick={() => navigate("/pricing")}
                variant="outline"
                className="rounded-full"
              >
                {user?.tier === "free" ? "Upgrade" : "Manage Plan"}
              </Button>
            </div>
          </div>

          {/* Replay Feature Tour */}
          <div className="card-omni p-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <RotateCcw className="w-5 h-5 text-primary" strokeWidth={1.5} />
                <div>
                  <h2 className="font-heading text-xl font-medium">Feature Tour</h2>
                  <p className="text-sm text-muted-foreground">Replay the guided walkthrough of Omni's features</p>
                </div>
              </div>
              <Button
                data-testid="replay-tour-btn"
                onClick={() => {
                  localStorage.removeItem("omni_tour_done");
                  toast.success("Tour will show on your next visit to Chat");
                  navigate("/chat");
                }}
                variant="outline"
                className="rounded-full gap-2"
              >
                <RotateCcw className="w-4 h-4" /> Replay Tour
              </Button>
            </div>
          </div>

          {/* Saved Sessions */}
          <div className="card-omni p-8">
            <div className="flex items-center gap-3 mb-4">
              <Save className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-xl font-medium">Saved Sessions</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Sessions are auto-saved when you log out. You can review or delete them here.
            </p>
            {savedSessions.length === 0 ? (
              <p className="text-sm text-muted-foreground/60 italic">No saved sessions yet.</p>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {savedSessions.map(s => (
                  <div key={s.id} data-testid={`session-${s.id}`} className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/30">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{s.module || "Chat"} — {s.conversation_title || "Untitled"}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(s.saved_at).toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        {s.draft_text && <span className="ml-2 text-primary">has draft</span>}
                      </p>
                    </div>
                    <button
                      data-testid={`delete-session-${s.id}`}
                      onClick={() => deleteSavedSession(s.id)}
                      className="p-2 rounded-xl hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Special Category Data Consent */}
          <div className="card-omni p-8" data-testid="special-consent-section">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-xl font-medium">Wellbeing Data Consent</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Mood journal, insights and therapy paths process special category data (emotional wellbeing) under
              GDPR Article 9, UK GDPR, PIPEDA and CPRA. Status:{" "}
              <span className={scConsent ? "text-green-600 font-medium" : "text-destructive font-medium"} data-testid="special-consent-status">
                {scConsent === null ? "..." : scConsent ? "Consent granted" : "Not granted"}
              </span>
            </p>
            {scConsent === true ? (
              <Button data-testid="withdraw-special-consent-btn" onClick={() => setSpecialConsent(false)} variant="outline" className="rounded-full">
                Withdraw Consent
              </Button>
            ) : scConsent === false ? (
              <Button data-testid="grant-special-consent-settings-btn" onClick={() => setSpecialConsent(true)} className="btn-omni bg-primary text-primary-foreground rounded-full">
                Grant Consent
              </Button>
            ) : null}
          </div>

          {/* Subscription Management */}
          <div className="card-omni p-8" data-testid="manage-subscription-section">
            <div className="flex items-center gap-3 mb-4">
              <Crown className="w-5 h-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-xl font-medium">Subscription</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Current plan: <span className="font-medium text-foreground capitalize">{user?.tier || "free"}</span>.
              You can cancel anytime — access continues until the end of your paid period, with no further charges.
            </p>
            <Button
              data-testid="cancel-subscription-btn"
              onClick={cancelSubscription}
              variant="outline"
              className="rounded-full"
            >
              Cancel Subscription
            </Button>
          </div>

          {/* Danger Zone */}
          <div className="card-omni p-8 border-destructive/20">
            <div className="flex items-center gap-3 mb-4">
              <Trash2 className="w-5 h-5 text-destructive" strokeWidth={1.5} />
              <h2 className="font-heading text-xl font-medium text-destructive">Danger Zone</h2>
            </div>

            {/* Delete History (keep account) */}
            <div className="mb-5 pb-5 border-b border-destructive/10">
              {!showDeleteHistory ? (
                <Button
                  data-testid="delete-history-toggle"
                  onClick={() => setShowDeleteHistory(true)}
                  variant="outline"
                  className="rounded-full border-destructive/50 text-destructive hover:bg-destructive/10"
                >
                  Delete My History
                </Button>
              ) : (
                <div className="p-4 bg-destructive/5 rounded-2xl space-y-3">
                  <p className="text-sm text-destructive">
                    This wipes your conversations, mood logs, memories, goals, bookmarks, recaps, calendar
                    events and backups — but your <strong>account, login, and subscription stay active</strong>.
                    This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <Button onClick={() => setShowDeleteHistory(false)} variant="outline" className="rounded-full">Cancel</Button>
                    <Button data-testid="confirm-delete-history-btn" onClick={deleteHistory} variant="destructive" className="rounded-full">
                      Delete History
                    </Button>
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-2">Use this anytime you want a fresh start without losing your account.</p>
            </div>

            {!showDelete ? (
              <Button
                data-testid="delete-account-toggle"
                onClick={() => setShowDelete(true)}
                variant="outline"
                className="rounded-full border-destructive/50 text-destructive hover:bg-destructive/10"
              >
                Delete Account
              </Button>
            ) : (
              <div className="p-4 bg-destructive/5 rounded-2xl space-y-3">
                <p className="text-sm text-destructive">
                  This will permanently delete your account and ALL associated data — conversations, mood logs, goals,
                  bookmarks, memories, therapy sessions, calendar events and backups. Payment records are anonymized and
                  retained only as required by financial regulations. This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <Button onClick={() => setShowDelete(false)} variant="outline" className="rounded-full">Cancel</Button>
                  <Button data-testid="confirm-delete-btn" onClick={deleteAccount} variant="destructive" className="rounded-full">
                    Delete Everything
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Logout */}
          <Button
            data-testid="settings-logout-btn"
            onClick={async () => {
              try {
                const draftData = {};
                for (let i = 0; i < localStorage.length; i++) {
                  const key = localStorage.key(i);
                  if (key?.startsWith("omni_draft_")) {
                    draftData[key.replace("omni_draft_", "")] = localStorage.getItem(key);
                  }
                }
                const activeConv = localStorage.getItem("omni_active_conv");
                const activeModule = localStorage.getItem("omni_active_module");
                await axios.post(`${API}/sessions/save`, {
                  conversation_id: activeConv || null,
                  module: activeModule || "friend",
                  drafts: draftData
                }, { headers });
                // Clean up drafts after saving
                Object.keys(draftData).forEach(k => localStorage.removeItem(`omni_draft_${k}`));
                localStorage.removeItem("omni_active_conv");
                localStorage.removeItem("omni_active_module");
              } catch { /* silent */ }
              logout();
              navigate("/");
            }}
            variant="ghost"
            className="w-full rounded-full gap-2 text-muted-foreground"
          >
            <LogOut className="w-4 h-4" /> Log Out
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
